import { Stack } from '@mui/material';
import { useMemo, useState } from 'react';
import { CalculusTab, ConditionsNew, ConditionState } from '@retail/calculus/types';
import { AllConditionsTable } from '@retail/calculus/components';
import { groupConditionsByBinding } from '@retail/calculus/utils';
import { useLanguageSelector } from '@retail/app/hooks';
import { AgreementBinding } from '@retail/purchase-agreements/types';
import { PriceComparisonDialog } from '@retail/purchase-agreements/containers';
import { LookupRoundingRuleDialog } from '@retail/rounding/containers';
import { AddButton, CalculusActionPortal, DeleteDialog } from '@retail/components';
import { useCalculusTFunction } from '@retail/calculus/i18n';
import { SupplierBase } from '@retail/suppliers/types';
import { useDisclosure } from '@shared/hooks';
import { useAvailableCalculusAreas, useConditionActions } from '@retail/calculus/context';
import { usePermissionsForCurrentUser } from '@retail/app/stores/selected-context';
import { CreateAllConditionsWizard } from './CreateAllConditions';

interface Props {
  conditions: ConditionsNew[];
  isFetching: boolean;
  selectedSupplier?: SupplierBase;
  conditionState?: ConditionState;
}

export function AllConditionsContainer({
  conditions,
  isFetching,
  selectedSupplier,
  conditionState,
}: Props) {
  const t = useCalculusTFunction();
  const { language } = useLanguageSelector();
  const { removeCondition } = useConditionActions();

  const { userCanCreate, userCanUpdate, userCanDelete } = usePermissionsForCurrentUser();
  const userCanCreateConditions = userCanCreate('PRICE_CALCULATION__COMBINED');
  const userCanUpdateConditions = userCanUpdate('PRICE_CALCULATION__COMBINED');
  const userCanDeleteConditions = userCanDelete('PRICE_CALCULATION__COMBINED');
  const showRounding = useAvailableCalculusAreas().includes(CalculusTab.ROUNDING);

  const groupedConditions = useMemo(() => groupConditionsByBinding(conditions), [conditions]);

  const [priceComparison, setPriceComparison] = useState<AgreementBinding | undefined>(undefined);
  const [simulationRuleId, setSimulationRuleId] = useState<number>();
  const [editCondition, setEditCondition] = useState<ConditionsNew | undefined>();
  const [deactivateConditionIds, setDeactivate] = useState<number[] | undefined>();
  const [deleteConditionIds, setDelete] = useState<number[] | undefined>();
  const { isOpen: isCreateOpen, onToggle: toggleCreate } = useDisclosure();

  return (
    <Stack flexGrow={1}>
      <CalculusActionPortal>
        {userCanCreateConditions && (
          <AddButton onClick={toggleCreate}>
            {t('calculus.createAllConditions.openButton')}
          </AddButton>
        )}
      </CalculusActionPortal>

      <AllConditionsTable
        conditions={groupedConditions}
        language={language}
        loading={isFetching}
        setPriceComparisonBinding={setPriceComparison}
        openPriceSimulation={setSimulationRuleId}
        editCondition={userCanUpdateConditions ? setEditCondition : undefined}
        deactivateCondition={userCanUpdateConditions ? setDeactivate : undefined}
        deleteCondition={userCanDeleteConditions ? setDelete : undefined}
        conditionState={conditionState}
        showRounding={showRounding}
      />
      {isCreateOpen && (
        <CreateAllConditionsWizard
          open
          selectedSupplier={selectedSupplier}
          showRounding={showRounding}
          onClose={toggleCreate}
        />
      )}
      {editCondition && (
        <CreateAllConditionsWizard
          open
          selectedSupplier={selectedSupplier}
          editCondition={editCondition}
          showRounding={showRounding}
          onClose={() => setEditCondition(undefined)}
        />
      )}
      {!!deactivateConditionIds && (
        <DeleteDialog
          isOpen
          dialogTitle={t('calculus.actions.deactivateCondition')}
          message={t('calculus.actions.confirmDeactivation')}
          onDelete={() => removeCondition(deactivateConditionIds, 'deactivate')}
          onClose={() => setDeactivate(undefined)}
        />
      )}
      {!!deleteConditionIds && (
        <DeleteDialog
          isOpen
          dialogTitle={t('calculus.actions.deleteFromDraft')}
          message={t('calculus.actions.confirmRemoval')}
          onDelete={() => removeCondition(deleteConditionIds, 'delete')}
          onClose={() => setDelete(undefined)}
        />
      )}
      {priceComparison && priceComparison.supplierNumber && (
        <PriceComparisonDialog
          open
          onClose={() => setPriceComparison(undefined)}
          defaultAgreementId={priceComparison.agreementId}
          supplierNumber={priceComparison.supplierNumber}
          levelProductGroupNr={priceComparison.productGroup?.groupNumber}
          levelMgItemNr={priceComparison.legacySupplierItem?.mgItemNumber}
        />
      )}
      {simulationRuleId && (
        <LookupRoundingRuleDialog
          open
          onClose={() => setSimulationRuleId(undefined)}
          priceRoundingRuleId={simulationRuleId}
        />
      )}
    </Stack>
  );
}
